import { isMobileApp } from "common/utils/browser";
import { Loader2, Palette, Sparkles } from "lucide-react";
import React from "react";
import { GradientButton } from "./GradientButton";

type PromptInputProps = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  isMobile?: boolean;
  onOpenMobileMenu?: () => void;
};

export const PromptInput = ({
  prompt,
  onPromptChange,
  onGenerate,
  isGenerating,
  isMobile = false,
  onOpenMobileMenu
}: PromptInputProps) => {
  const handleGenerate = () => {
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
    onGenerate();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isGenerating && prompt.trim()) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div
      className={`${isMobile ? "p-4 pb-2" : "p-6 pb-3"} ${isMobileApp() ? "fixed bottom-0 left-0 right-0 z-50 text-center shadow-lg" : ""} bg-white`}
    >
      <div className={`flex ${isMobile ? "gap-3" : "gap-4"}`}>
        <div className="relative flex-1">
          <input
            type="text"
            data-testid="prompt-input"
            value={prompt}
            onChange={e => onPromptChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isMobile
                ? "Describe your design idea"
                : "Describe your design idea... e.g., 'A happy avocado doing yoga'"
            }
            className={`w-full rounded-sm border border-gray-300 bg-gray-50 px-4 py-4 text-base transition-all focus:outline-none ${
              isMobile ? "pr-16" : "pr-44"
            }`}
          />
          <GradientButton
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            className="absolute right-2 top-1/2 flex -translate-y-1/2"
            style={!prompt.trim() || isGenerating ? {} : { color: "white" }}
            data-testid="generate-button"
          >
            {isGenerating ? (
              <>
                <Loader2 className={`${isMobile ? "h-4 w-4" : "h-4 w-4"} animate-spin`} />
                {!isMobile && <span>Generating...</span>}
              </>
            ) : (
              <>
                <Sparkles className={`${isMobile ? "h-4 w-4" : "h-4 w-4"}`} />
                {isMobile ? null : <span>Design with AI</span>}
              </>
            )}
          </GradientButton>
        </div>
        {isMobile && onOpenMobileMenu ? (
          <button
            data-testid="mobile-menu-button"
            onClick={onOpenMobileMenu}
            className="flex items-center justify-center rounded-sm border border-gray-300 bg-[#F9FAFC] px-3 py-2 transition-all duration-200"
            style={{ color: "#888" }}
            aria-label="Open design options palette"
          >
            <Palette className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
